import { useEffect, useRef, useState } from "react";
import type { MessageDirect } from "../contract";
import { type EtatFlux, ouvrirFlux } from "./flux";

/**
 * Le flux du direct pour un composant (lot Q1) : ouvre `url` au montage, ferme au demontage, rend l'etat de la
 * connexion et le dernier message recu.
 *
 * Les rappels sont pour ce que l'etat React ne dit pas bien : deux changements d'etat rapproches se
 * regroupent en un seul rendu, un rappel les voit tous.
 * - `onMessage` : chaque message du bus, dans l'ordre ;
 * - `onEtat` : chaque changement d'etat de la connexion (`connexion`, `connecte`, `coupe`) ;
 * - `onRelire` : le point d'accroche de la regie. Appele quand le serveur dit `reprise` (sa memoire a change) ou
 *   quand des messages ont ete manques : relire l'etat. **Il ne couvre pas tout** : la relecture de Twitch
 *   (`reconcile`) ne publie rien sur le bus, la regie relit donc aussi son etat toutes les 30 s (PR E,
 *   `CLAUDE.app.md`).
 *
 * `url` a `null` : rien n'est ouvert (la cle manque, la page n'est pas dans OBS). Les rappels peuvent changer
 * a chaque rendu sans rouvrir le flux ; seuls `url` et `entetes` le font.
 */

export type RappelsFlux = {
  onMessage?: ((message: MessageDirect) => void) | undefined;
  onEtat?: ((etat: EtatFlux) => void) | undefined;
  onRelire?: (() => void) | undefined;
  /** Que faire d'un 401 ou 403 : `arret` (overlay, par defaut) ou `relance-lente` (regie, 60 s). */
  surRefus?: "arret" | "relance-lente" | undefined;
};

export function useFlux(
  url: string | null,
  entetes?: Record<string, string>,
  rappels: RappelsFlux = {},
): { etat: EtatFlux; dernier: MessageDirect | null } {
  const [etat, setEtat] = useState<EtatFlux>({ genre: "connexion" });
  const [dernier, setDernier] = useState<MessageDirect | null>(null);
  const rappelsRef = useRef(rappels);
  rappelsRef.current = rappels;
  // Les en-tetes se comparent par leur contenu : un objet neuf a chaque rendu ne doit pas rouvrir le flux.
  const cleEntetes = JSON.stringify(entetes ?? {});
  const surRefus = rappels.surRefus;

  useEffect(() => {
    if (url === null) return;
    const autres = JSON.parse(cleEntetes) as Record<string, string>;
    setEtat({ genre: "connexion" });
    const flux = ouvrirFlux({
      url,
      entetes: autres,
      surMessage: (message) => {
        setDernier(message);
        rappelsRef.current.onMessage?.(message);
      },
      surEtat: (suivant) => {
        setEtat(suivant);
        rappelsRef.current.onEtat?.(suivant);
      },
      surReprise: () => rappelsRef.current.onRelire?.(),
      surRefus,
    });
    return () => flux.fermer();
  }, [url, cleEntetes, surRefus]);

  return { etat, dernier };
}
